import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { addSuppressedError } from '@shared/utils/logger';
import { DesignLayoutType, EventPageType, PageKind } from '@graphql/generated-base';
import { isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import type { GuestSiteEvent, GuestSiteEventDesign } from './layout.types';
import { darkenColor, isLightColor } from '@shared/utils/color';
import { toRgbArray } from '@shared/utils/color';
import type { ButtonV2Props } from '@withjoy/joykit';
import { defaultTheme } from '@withjoy/joykit';
import chroma from 'chroma-js';
import { fadeColor } from '@shared/utils/color';
import { ART_PLACEHOLDER_MOBILE_URL } from '@shared/constants/websiteDesigner';
import { isInIframe } from '@shared/utils/isInIframe';
import { addRendition } from '@shared/utils/photoRendition';
import type { SupportedHeaderPhotoLayoutSchema, WebsitePhotoCropInfo, WebsitePhotoBaseProperties } from '@shared/types/WebsiteHeaderSettings.types';

export const ART_PLACEHOLDER_URL = 'https://withjoy.com/assets/public/art-placeholder.png' as const;

const isPreviewing = isInIframe();

export const getCustomArtMobileUrl = (eventDesign?: GuestSiteEventDesign): string | null => {
  if (!eventDesign) {
    return null;
  }
  if (eventDesign?.useCustomTheme) {
    return eventDesign?.customTheme?.mobileThemeAsset?.assetURL ?? ART_PLACEHOLDER_MOBILE_URL;
  }
  return null;
};

export const getIsCustomArtEnabled = (eventDesign: GuestSiteEventDesign, isMobile?: boolean): boolean => {
  const desktopUrl = !!eventDesign?.customTheme?.desktopThemeAsset?.assetURL;
  const mobileUrl = !!eventDesign?.customTheme?.mobileThemeAsset?.assetURL;
  const useCustomTheme = !!eventDesign?.useCustomTheme;
  if (isMobile) {
    return isPreviewing ? useCustomTheme : useCustomTheme && mobileUrl;
  }
  return isPreviewing ? useCustomTheme : useCustomTheme && desktopUrl;
};
const nonSupportedEventPageSet = new Set([EventPageType.guests, EventPageType.tidbits]);
export const isSupportedEventPage = (pageType: EventPageType) => {
  return !nonSupportedEventPageSet.has(pageType);
};

export const findEventPageByPageSlug = (
  pages: ReadonlyArray<EventPageFragment>,
  target: string,
  isCustomPage?: boolean,
  isUrlBasedCustomPath?: boolean,
): Readonly<EventPageFragment> | undefined => {
  const matchesSlug = (page: EventPageFragment) => isSupportedEventPage(page.type) && page.pageSlug.toLocaleLowerCase() === target.toLocaleLowerCase();
  // CORE-6470: "is a custom page" means any non-legacy kind (systemCustom | userCustom).
  const matchesType = (page: EventPageFragment) => !isCustomPage || page.pageKind !== PageKind.systemLegacy;

  // CORE-6456: a slug can be claimed by both a canonical page (a native typed page, or the
  // system/`default` migrated custom page) AND a userGenerated "squatter" custom page (a
  // user-named page that happens to collide with a built-in slug). Disambiguate by URL:
  //   - the explicit `/page/<slug>` URL targets the squatter,
  //   - every other (canonical/migrated) resolution targets the canonical page, so e.g.
  //     `/schedule` shows the real schedule rather than the squatter.
  // When no squatter exists for the slug (the overwhelmingly common case) this falls through
  // to the original `matchesSlug && matchesType` lookup — exact prior behavior, no regression.
  const squatterMatch = pages.find(page => matchesSlug(page) && isUserGeneratedMigratedSquatter(page));
  if (squatterMatch) {
    if (isUrlBasedCustomPath) return squatterMatch;
    const canonicalMatch = pages.find(page => matchesSlug(page) && !isUserGeneratedMigratedSquatter(page));
    return canonicalMatch ?? (isCustomPage ? squatterMatch : undefined);
  }

  return pages.find(page => matchesSlug(page) && matchesType(page));
};

export const findEventPageByType = (pages: ReadonlyArray<EventPageFragment>, target: EventPageType): Readonly<EventPageFragment> | undefined => {
  return pages.find(page => isSupportedEventPage(page.type) && page.type === target);
};

// CORE-6456: resolves the draft page a preview edit (e.g. a cover-photo update) targets. The admin
// identifies the page by its EventPageType plus — for custom pages — its slug.
//
// Custom pages match on the **exact** pageSlug, NOT the case-insensitive `findEventPageByPageSlug`
// guest-router lookup. `(event, slug)` is unique per container, so the exact slug uniquely
// identifies the edited page even when several pages canonicalize to the same migrated slug — e.g.
// an event with both a systemCustom `schedule` (default) and a userCustom `Schedule` (userGenerated)
// squatter (the case-sensitive unique constraint lets these coexist). A case-insensitive lookup
// would prefer the squatter and mis-target the systemCustom page's edit. Native pages resolve by type.
export const findPreviewEditTargetPage = (
  pages: ReadonlyArray<EventPageFragment>,
  pageType: EventPageType,
  pageSlug: string | undefined,
): Readonly<EventPageFragment> | undefined =>
  pageType === EventPageType.custom && pageSlug ? pages.find(page => page.type === EventPageType.custom && page.pageSlug === pageSlug) : findEventPageByType(pages, pageType);

// Deliberately does NOT return `pages`: every caller supplies its own nav-safe list from
// `useGuestSitePages` (migrated-legacy + RSVP filtering), and returning a differently-filtered
// list here only invited callers to trust a value they always override (LEGRSVP-96).
export const getEventMenuPropsFromEvent = (event: GuestSiteEvent) => {
  return {
    eventId: event?.id || '',
    layoutType: event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha,
    dateInMilliseconds: event.info?.dateV0918?.milliseconds || 0,
    brannanNavigation: event?.eventDesign?.brannanNavigation,
    isCustomArtEnabled: getIsCustomArtEnabled(event?.eventDesign, true),
    customArtUrl: addRendition({ url: getCustomArtMobileUrl(event?.eventDesign), renditionSize: 'medium' }),
  };
};

export const getButtonPropsBasedOnBackground = (backgroundColor: string) => {
  const buttonProps: Partial<ButtonV2Props> = {};
  const { colors } = defaultTheme;

  if (backgroundColor) {
    buttonProps.size = 'lg';
    buttonProps.border = `1px soild ${colors.gray5}`;
    buttonProps.shape = 'rounded';

    if (isLightColor(...toRgbArray(backgroundColor))) {
      buttonProps.variant = 'solid';
      buttonProps.backgroundColor = colors.black;
      buttonProps._hover = {
        backgroundColor: fadeColor(colors.black, 0.8),
        color: fadeColor(colors.white, 0.8),
      };
      buttonProps._active = {
        backgroundColor: fadeColor(colors.black, 0.9),
        color: fadeColor(colors.white, 0.9),
      };
    } else {
      buttonProps.variant = 'outline';
      buttonProps.backgroundColor = colors.white;
      buttonProps.color = colors.black;
      buttonProps._hover = {
        backgroundColor: fadeColor(colors.white, 0.8),
        color: fadeColor(colors.black, 0.8),
      };
      buttonProps._active = {
        backgroundColor: fadeColor(colors.white, 0.9),
        color: fadeColor(colors.black, 0.9),
      };
    }
  }

  return buttonProps;
};

export const getHoverColor = (color: string): string => {
  const darkenedColor = darkenColor(color);
  return chroma(darkenedColor).brighten(0.2).hex();
};

export const getActiveColor = (color: string): string => {
  const darkenedColor = darkenColor(color);
  return chroma(darkenedColor).darken(0.2).hex();
};

export const getSolidButtonStylesWithAccentAndTextColor = (accentColor: string, textColor: string): ButtonV2Props => {
  const darkenedColor = darkenColor(accentColor);
  const backgroundColorHover = chroma(accentColor).brighten(0.2).hex();
  const backgroundColorActive = chroma(accentColor).darken(0.2).hex();

  return {
    backgroundColor: darkenedColor,
    _hover: { color: textColor, backgroundColor: backgroundColorHover },
    _active: { color: textColor, backgroundColor: backgroundColorActive },
  };
};

export const getButtonOutlineStylesWithAccentColorAndTextColor = (accentColor: string): ButtonV2Props => {
  const darkenedColor = darkenColor(accentColor);
  const hoverColor = getHoverColor(accentColor);
  const activeColor = getActiveColor(accentColor);
  const backgroundColorHover = chroma(accentColor).alpha(0.1).hex();
  const backgroundColorActive = chroma(accentColor).alpha(0.2).hex();
  const disabledColor = chroma(accentColor).alpha(0.5).hex();

  return {
    borderColor: darkenedColor,
    color: darkenedColor,
    _hover: { borderColor: hoverColor, color: hoverColor, backgroundColor: backgroundColorHover },
    _active: { borderColor: activeColor, color: activeColor, backgroundColor: backgroundColorActive },
    _disabled: { borderColor: disabledColor, color: disabledColor, backgroundColor: backgroundColorHover },
  };
};

export const getButtonStylesWithAccentColor = (accentColor: string): ButtonV2Props => {
  const hoverColor = getHoverColor(accentColor);
  const activeColor = getActiveColor(accentColor);
  const backgroundColorHover = chroma(accentColor).alpha(0.1).hex();
  const backgroundColorActive = chroma(accentColor).alpha(0.2).hex();
  const disabledColor = chroma(accentColor).alpha(0.5).hex();

  return {
    borderColor: accentColor,
    color: accentColor,
    _hover: { borderColor: hoverColor, color: hoverColor, backgroundColor: backgroundColorHover },
    _active: { borderColor: activeColor, color: activeColor, backgroundColor: backgroundColorActive },
    _disabled: { borderColor: disabledColor, color: disabledColor, backgroundColor: backgroundColorHover },
  };
};

export const getImageRendition = (url: string) => {
  try {
    const imageUrl = new URL(url);
    imageUrl.searchParams.append('rendition', 'medium');
    return imageUrl.toString();
  } catch (error) {
    addSuppressedError(error, { context: 'getImageRendition', url });
    return url;
  }
};

export const getHeaderPhotoAspectRatio = (cropInfo?: WebsitePhotoCropInfo, photo?: WebsitePhotoBaseProperties) => {
  if (cropInfo && photo) {
    let cropWidth = cropInfo.width;
    let cropHeight = cropInfo.height;
    if (cropInfo?.unit === 'percent') {
      cropWidth = (cropInfo.width / 100) * photo.width;
      cropHeight = (cropInfo.height / 100) * photo.height;
    }
    const aspectRaio = cropWidth / cropHeight;
    return aspectRaio;
  }
  // retuns default 16/9 if photoPresentation cropInfo is not available
  return 16 / 9;
};

type HeaderLayoutWithJsonContainerId<T> = T extends { jsonContainerId?: unknown } ? T & { jsonContainerId: T['jsonContainerId'] } : never;

export const isHeaderLayoutWithJsonContainerId = <T extends SupportedHeaderPhotoLayoutSchema>(layout: T): layout is HeaderLayoutWithJsonContainerId<T> => {
  return Boolean('jsonContainerId' in layout);
};

export const getCustomArtUrl = (eventDesign?: GuestSiteEventDesign, _layoutType?: DesignLayoutType): string => {
  if (!eventDesign) {
    return ART_PLACEHOLDER_URL;
  }
  if (eventDesign?.useCustomTheme) {
    return eventDesign?.customTheme?.desktopThemeAsset?.assetURL || ART_PLACEHOLDER_URL;
  }
  return ART_PLACEHOLDER_URL;
};

// Returns the URL when a guest navbar/menu should hard-nav out of the guest layout into the
// standalone registry items app on a multi-page layout. Matches both:
// - legacy EventPageType.registry pages (non-migrated events), and
// - migrated custom pages that replace the registry (type=custom, pageSlug canonicalizes to 'registrylinks').
// Excludes userCustom squatters — user-created pages that happen to share the 'registry'
// slug are ordinary custom pages and must stay SPA-routed.
// Returns undefined when the link should stay SPA-routed — single-page/scroll layouts,
// user-generated squatters, or any non-registry page.
// Gated by the `registryEnableGuestNavRegistryLinks` feature flag at call sites: when ON,
// callers skip this helper and SPA-route to /registrylinks via eventPageRoute.goToPath.
export const getNativeRegistryHardNavUrl = (
  pageType: string,
  pageSlug: string,
  pageKind: string,
  isSinglePage: boolean,
  eventHandle: string,
  queryString = '',
): string | undefined => {
  if (isSinglePage) return undefined;
  // A userCustom page squatting the 'registry' slug is an ordinary custom page — not the registry.
  if (pageKind === PageKind.userCustom && toCanonicalMigratedSlug(pageSlug) === 'registrylinks') return undefined;
  const isRegistry = pageType === 'registry' || toCanonicalMigratedSlug(pageSlug) === 'registrylinks';
  if (!isRegistry) return undefined;
  return `/${eventHandle}/registry${queryString}`;
};
